/**
 * StoreTable Component
 * 
 * Displays stores in a table format with sorting, filtering, and actions.
 * Used in the Store Management screen for managers.
 * 
 * @module StoreTable
 */

import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { SortableFilterableTableHeader, ColumnFilter, ColumnSort } from '../common/SortableFilterableTableHeader';
import {
  CheckCircle,
  XCircle,
  Clock,
  Archive,
  Eye,
  Edit,
  UserCheck,
  MapPin,
  MoreVertical,
  Trash2
} from 'lucide-react';
import { Store, StoreStatus } from '../../@types/store';
import { ThemeColors, Spacing, Typography, Shadows, Transitions } from '../../styles/ThemeStyles';
import { StoreService } from '../../services/StoreService';

/**
 * StoreTable props interface
 */
export interface StoreTableProps {
  /** Stores to display */
  stores: Store[];
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Handle row click */
  onRowClick?: (store: Store) => void;
  
  /** Handle edit action */
  onEdit?: (store: Store) => void;
  
  /** Handle delete action */
  onDelete?: (store: Store) => void;
  
  /** Handle view action */
  onView?: (store: Store) => void;
  
  /** Handle assign sales rep action */
  onAssignSalesRep?: (store: Store) => void;
  
  /** Enable row selection */
  selectable?: boolean;
  
  /** Selected store IDs */
  selectedStoreIds?: string[];
  
  /** Handle selection change */
  onSelectionChange?: (storeIds: string[]) => void;
  
  /** Show actions column */
  showActions?: boolean;
  
  /** Column filters */
  columnFilters?: ColumnFilter[];
  
  /** Column sort */
  columnSort?: ColumnSort | null;
  
  /** Handle filter change */
  onFilterChange?: (filters: ColumnFilter[]) => void;
  
  /** Handle sort change */
  onSortChange?: (sort: ColumnSort | null) => void;
  
  /** Select all handler */
  onSelectAll?: (checked: boolean) => void;
}

/**
 * Styled components
 */
const TableContainer = styled.div`
  width: 100%;
  background: white;
  border-radius: 12px;
  box-shadow: ${Shadows.sm};
  position: relative;
  overflow: visible;
`;

const TableScrollArea = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  border-radius: inherit;
  position: relative;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 700px;
  position: relative;
`;

const TableHead = styled.thead`
  background-color: ${ThemeColors.background.secondary};
`;

const TableRow = styled.tr<{ clickable?: boolean; selected?: boolean }>`
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  background-color: ${props => props.selected ? ThemeColors.primary.light : 'white'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.selected 
      ? ThemeColors.primary.light 
      : ThemeColors.background.secondary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${ThemeColors.border.light};
  }
`;

const TableHeader = styled.th`
  padding: ${Spacing.sm} ${Spacing.md};
  text-align: left;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;

  &:first-child {
    border-radius: 12px 0 0 0;
  }

  &:last-child {
    border-radius: 0 12px 0 0;
  }
`;

const TableCell = styled.td`
  padding: ${Spacing.sm} ${Spacing.md};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.primary};
  vertical-align: middle;
`;

const StatusBadge = styled.span<{ status?: StoreStatus }>`
  display: inline-flex;
  align-items: center;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 20px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  gap: ${Spacing.xs};
  background-color: ${props => {
    switch (props.status) {
      case 'active': return ThemeColors.success.light;
      case 'inactive': return ThemeColors.error.light;
      case 'pending': return ThemeColors.warning.light;
      case 'archived': return ThemeColors.background.secondary;
      default: return ThemeColors.background.secondary;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return ThemeColors.success.dark;
      case 'inactive': return ThemeColors.error.dark;
      case 'pending': return ThemeColors.warning.dark;
      case 'archived': return ThemeColors.text.secondary;
      default: return ThemeColors.text.primary;
    }
  }};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${ThemeColors.text.secondary};
  transition: all 0.2s ease;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${ThemeColors.background.secondary};
    color: ${ThemeColors.primary.main};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ActionsCell = styled(TableCell)`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${ThemeColors.primary.main};
`;

const EmptyState = styled.div`
  padding: ${Spacing.xxl} ${Spacing.lg};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

const LoadingRow = styled(TableRow)`
  background: linear-gradient(
    90deg,
    ${ThemeColors.background.secondary} 25%,
    ${ThemeColors.background.secondary} 50%,
    ${ThemeColors.background.secondary} 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const StoreName = styled.div`
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin-bottom: 1px;
  font-size: ${Typography.fontSize.xs};
`;

const StoreInfo = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 10px;
    height: 10px;
  }
`;

const CustomerNumber = styled.span`
  font-family: ${Typography.fontFamily.monospace};
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.xs};
`;

const NotesBadge = styled.div<{ count: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 12px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${props => props.count > 0 ? '#ffffff' : ThemeColors.text.secondary};
  background-color: ${props => {
    if (props.count === 0) return ThemeColors.gray[100];
    if (props.count >= 5) return ThemeColors.success.main;
    if (props.count >= 3) return ThemeColors.info.main;
    return ThemeColors.primary.main;
  }};
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  border: 1px solid ${props => props.count > 0 ? ThemeColors.info.main : ThemeColors.gray[200]};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeOut};
  
  &:hover {
    background-color: ${props => props.count > 0 ? ThemeColors.info.dark : ThemeColors.gray[200]};
  }
`;

/**
 * Get status icon based on store status
 */
const getStatusIcon = (status?: StoreStatus) => {
  switch (status) {
    case 'active':
      return <CheckCircle />;
    case 'inactive':
      return <XCircle />;
    case 'pending':
      return <Clock />;
    case 'archived':
      return <Archive />;
    default:
      return <Clock />;
  }
};

/**
 * Format date for display
 */
const formatDate = (date?: Date | any): string => {
  if (!date) return 'Never';
  
  try {
    let d: Date;
    if (date instanceof Date) {
      d = date;
    } else if (date && typeof date === 'object' && 'toDate' in date && typeof date.toDate === 'function') {
      d = date.toDate();
    } else if (date && typeof date === 'object' && 'seconds' in date) {
      // Firestore Timestamp
      d = new Date(date.seconds * 1000);
    } else {
      d = new Date(date);
    }
    
    // Check if date is valid
    if (isNaN(d.getTime())) {
      console.warn('[StoreTable] Invalid date:', date);
      return 'Never';
    }
    
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  } catch (error) {
    console.error('[StoreTable] Error formatting date:', error, date);
    return 'Never';
  }
};

/**
 * StoreTable Component
 * 
 * @example
 * ```tsx
 * <StoreTable
 *   stores={stores}
 *   isLoading={isLoading}
 *   onRowClick={(store) => navigate(`/store/${store.id}`)}
 *   onEdit={handleEdit}
 *   onAssignSalesRep={handleAssign}
 *   showActions
 * />
 * ```
 */
/**
 * Get display value for store sales group
 */
const getStoreSalesGroupDisplay = (store: Store): string => {
  const salesGroupValue =
    (store.SalesGroup ?? store.salesGroup ?? '').toString().trim();

  if (salesGroupValue) {
    return salesGroupValue;
  }

  return 'Unassigned';
};

export const StoreTable: React.FC<StoreTableProps> = ({
  stores,
  isLoading = false,
  onRowClick,
  onEdit,
  onDelete,
  onView,
  onAssignSalesRep,
  selectable = false,
  selectedStoreIds = [],
  onSelectionChange,
  showActions = true,
  columnFilters = [],
  columnSort = null,
  onFilterChange,
  onSortChange,
  onSelectAll
}) => {
  const { t } = useTranslation();
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedStoreIds);
  const scrollContainerIdRef = useRef(`store-table-${Math.random().toString(36).slice(2)}`);
  const scrollContainerId = scrollContainerIdRef.current;

  // Define columns for SortableFilterableTableHeader
  // dbField maps the column key to the database field name
  const tableColumns = [
    { key: 'customerNo', label: 'Customer #', filterable: true, sortable: true, dbField: 'CustomerNo' },
    { key: 'name', label: 'Store Name', filterable: true, sortable: true, dbField: 'StoreName' },
    { key: 'salespersonNo', label: 'Sales Person', filterable: true, sortable: true, dbField: 'SalespersonNo' },
    { key: 'city', label: 'Location', filterable: true, sortable: true, dbField: 'City' },
    { key: 'salesGroup', label: 'Sales Group', filterable: true, sortable: true, dbField: 'SalesGroup' },
    { key: 'sortField', label: 'Sort Field', filterable: true, sortable: true, dbField: 'SortField' },
    { key: 'storeNumber', label: 'Store Number', filterable: true, sortable: true, dbField: 'StoreNumber' },
    { key: 'lastVisit', label: 'Last Visit', filterable: false, sortable: false }, // Temporarily disabled
    { key: 'notesCount', label: 'Visits', filterable: false, sortable: false }, // Temporarily disabled
    ...(showActions ? [{ key: 'actions', label: 'Actions', filterable: false, sortable: false }] : [])
  ];

  // Prepare table data for filter/sort component
  const tableData = useMemo(() => {
    return stores.map(store => {
      // Use lastNoteDate if available, otherwise use lastVisit
      const lastDate = store.lastNoteDate || store.lastVisit;
      const lastVisitDisplay = lastDate ? formatDate(lastDate) : 'Never';
      
      return {
        customerNo: store.customerNo || '',
        storeNumber: store.StoreNumber || store.storeNumber || '',
        name: store.name || '',
        salespersonNo: store.salespersonNo || store.salesPersonName || 'Unassigned',
        city: store.city || '',
        salesGroup: getStoreSalesGroupDisplay(store),
        sortField: store.sortField || (store as any).SortField || '',
        lastVisit: lastVisitDisplay, // Use formatted date string for filtering
        notesCount: store.notesCount || 0
      };
    });
  }, [stores]);

  // Filter and sort stores based on column filters and sort
  // Best Practice: Most filtering is done at database level (Firestore)
    // Only apply client-side filtering for computed fields (like date ranges from visits)
  const filteredAndSortedStores = useMemo(() => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'StoreTable.tsx:453',message:'FilteredAndSortedStores calculation start',data:{storesCount:stores.length,columnFiltersCount:columnFilters.length,columnFilters:columnFilters.map(f=>({column:f.column,hasValue:!!f.value,hasDateRange:!!f.dateRange}))},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    // Stores are already filtered at database level for most fields
    // Only apply client-side filtering for fields that can't be filtered in Firestore
    let filtered = [...stores];

    // Apply client-side filters only for:
    // 1. Date range filters (computed from notes, can't be filtered in Firestore easily)
    // 2. Customer number and name filters (if using search term, already handled)
    columnFilters.forEach(filter => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'StoreTable.tsx:462',message:'Processing column filter',data:{column:filter.column,hasValue:!!filter.value,hasDateRange:!!filter.dateRange,value:filter.value},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      // Handle date range filters (client-side only - computed from visits)
      if (filter.dateRange && (filter.dateRange.from || filter.dateRange.to)) {
        filtered = filtered.filter(store => {
          const lastDate = store.lastNoteDate || store.lastVisit;
          if (!lastDate) {
            // If no date and filter requires dates, exclude this store
            return false;
          }
          
          let dateValue: Date;
          if (lastDate instanceof Date) {
            dateValue = lastDate;
          } else if (lastDate && typeof lastDate === 'object' && 'toDate' in lastDate && typeof lastDate.toDate === 'function') {
            dateValue = lastDate.toDate();
          } else {
            dateValue = new Date(lastDate as any);
          }
          const dateStr = dateValue.toISOString().split('T')[0]; // YYYY-MM-DD format
          
          // Check if date is within range
          if (filter.dateRange?.from && dateStr < filter.dateRange.from) {
            return false;
          }
          if (filter.dateRange?.to && dateStr > filter.dateRange.to) {
            return false;
          }
          
          return true; // Date is within range
        });
      }
      
      // Apply client-side filters for all column filters when stores are already client-side filtered
      // (e.g., when filtered by notes). This is necessary because database-level filtering
      // only works when fetching from DB, not when stores are pre-filtered client-side.
      if (filter.value && !filter.dateRange) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'StoreTable.tsx:495',message:'Applying client-side column filter',data:{column:filter.column,value:filter.value,filteredCountBefore:filtered.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        const filterValues = Array.isArray(filter.value) ? filter.value : [filter.value];
        filtered = filtered.filter(store => {
          let storeValue: string = '';
          
          switch (filter.column) {
            case 'customerNo':
              storeValue = store.customerNo || '';
              break;
            case 'storeNumber':
              storeValue = store.StoreNumber || store.storeNumber || '';
              break;
            case 'name':
              storeValue = store.name || '';
              break;
            case 'salespersonNo':
              storeValue = store.salespersonNo || store.salesPersonName || 'Unassigned';
              break;
            case 'city':
              storeValue = store.city || '';
              break;
            case 'salesGroup':
              storeValue = getStoreSalesGroupDisplay(store);
              break;
            case 'sortField':
              storeValue = store.sortField || (store as any).SortField || '';
              break;
            default:
              storeValue = String((store as any)[filter.column] || '');
          }
          
          const storeValueLower = storeValue.toLowerCase();
          return filterValues.some(filterVal => {
            const filterValStr = String(filterVal || '').toLowerCase();
            return storeValueLower === filterValStr;
          });
        });
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'StoreTable.tsx:525',message:'After applying client-side column filter',data:{column:filter.column,filteredCountAfter:filtered.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
      }
      
      // Note: When stores come from database, other filters (city, state, salesGroup, salespersonNo) 
      // are already applied at database level in StoreService.getStores().
      // However, when stores are pre-filtered client-side (e.g., by notes filter),
      // we need to apply column filters client-side as well.
    });

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'StoreTable.tsx:535',message:'FilteredAndSortedStores calculation end',data:{finalCount:filtered.length,originalCount:stores.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion

    // IMPORTANT: Sorting is already applied at database level in StoreService.getStores()
    // The stores returned from the database are already sorted according to columnSort
    // Client-side sorting would only sort the currently loaded stores (e.g., first 50),
    // not all stores in the database, which is incorrect behavior.
    // 
    // Only apply client-side sorting for computed fields that CANNOT be sorted in Firestore:
    // - notesCount: computed from visits subcollection, cannot be sorted at DB level
    // - lastVisit: computed from visits, but may have a DB field - check if DB sorting works first
    // 
    // For all other fields (customerNo, name, city, salesGroup, salespersonNo), 
    // rely on database-level sorting to ensure ALL stores are sorted correctly.
    // NOTE: All sorting is now done at database level using denormalized fields
    // (notesCount and lastVisit are stored on store documents)
    // No client-side sorting needed - stores come pre-sorted from the database
    // This provides fast, accurate sorting across all stores, not just loaded ones

    return filtered;
  }, [stores, columnFilters, columnSort]);

  /**
   * Handle row selection
   */
  const handleRowSelect = (storeId: string, checked: boolean) => {
    const newSelection = checked
      ? [...localSelectedIds, storeId]
      : localSelectedIds.filter(id => id !== storeId);
    
    setLocalSelectedIds(newSelection);
    onSelectionChange?.(newSelection);
  };

  /**
   * Handle select all
   */
  const handleSelectAll = (checked: boolean) => {
    const newSelection = checked ? filteredAndSortedStores.map(s => s.id) : [];
    setLocalSelectedIds(newSelection);
    onSelectionChange?.(newSelection);
    onSelectAll?.(checked);
  };

  /**
   * Check if all rows are selected
   */
  const allSelected = useMemo(() => {
    return filteredAndSortedStores.length > 0 && localSelectedIds.length === filteredAndSortedStores.length;
  }, [filteredAndSortedStores, localSelectedIds]);

  if (isLoading) {
    return (
      <TableContainer>
        <TableScrollArea data-scroll-container={scrollContainerId}>
          <Table>
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableHeader>
                    <Checkbox
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      title={allSelected ? 'Deselect all' : 'Select all'}
                    />
                  </TableHeader>
                )}
                <SortableFilterableTableHeader
                  scrollContainerId={scrollContainerId}
                  columns={tableColumns}
                  data={[]}
                  onFilterChange={onFilterChange || (() => {})}
                  onSortChange={onSortChange || (() => {})}
                  currentFilters={columnFilters}
                  currentSort={columnSort}
                  renderAsRow={true}
                  onLoadFilterValues={async (columnKey: string, dbField: string) => {
                    // Load unique filter values from database
                    try {
                      return await StoreService.getUniqueFilterValues(dbField, 1000);
                    } catch (error) {
                      console.error(`Failed to load filter values for ${columnKey}:`, error);
                      return [];
                    }
                  }}
                />
              </TableRow>
            </TableHead>
            <tbody>
              {[1, 2, 3, 4, 5].map((i) => (
                <LoadingRow key={i}>
                  {selectable && <TableCell></TableCell>}
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  {showActions && <TableCell></TableCell>}
                </LoadingRow>
              ))}
            </tbody>
          </Table>
        </TableScrollArea>
      </TableContainer>
    );
  }

  if (filteredAndSortedStores.length === 0 && !isLoading) {
    return (
      <TableContainer>
        <EmptyState>
          <p>No stores found</p>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <TableScrollArea data-scroll-container={scrollContainerId}>
        <Table>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableHeader>
                  <Checkbox
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    title={allSelected ? 'Deselect all' : 'Select all'}
                  />
                </TableHeader>
              )}
              <SortableFilterableTableHeader
                scrollContainerId={scrollContainerId}
                columns={tableColumns}
                data={tableData}
                onFilterChange={onFilterChange || (() => {})}
                onSortChange={onSortChange || (() => {})}
                currentFilters={columnFilters}
                currentSort={columnSort}
                renderAsRow={true}
                onLoadFilterValues={async (columnKey: string, dbField: string, searchTerm?: string) => {
                  // Load unique filter values from database
                  // When searchTerm is provided, queries database with search term
                  // This ensures search works across entire database, not just displayed stores
                  try {
                    return await StoreService.getUniqueFilterValues(dbField, 1000, searchTerm);
                  } catch (error) {
                    console.error(`Failed to load filter values for ${columnKey}:`, error);
                    return [];
                  }
                }}
              />
            </TableRow>
          </TableHead>
          <tbody>
            {filteredAndSortedStores.map((store) => (
              <TableRow
                key={store.id}
                clickable={!!onRowClick}
                selected={localSelectedIds.includes(store.id)}
                onClick={() => onRowClick?.(store)}
              >
                {selectable && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      type="checkbox"
                      checked={localSelectedIds.includes(store.id)}
                      onChange={(e) => handleRowSelect(store.id, e.target.checked)}
                    />
                  </TableCell>
                )}
                <TableCell>
                  <CustomerNumber>{store.customerNo}</CustomerNumber>
                </TableCell>
                <TableCell>
                  <StoreName>{store.name}</StoreName>
                  {store.phone && (
                    <StoreInfo>{store.phone}</StoreInfo>
                  )}
                </TableCell>
                <TableCell>
                  {store.salespersonNo || store.salesPersonName || 'Unassigned'}
                </TableCell>
                <TableCell>
                  <div>{store.city || 'N/A'}{store.state && `, ${store.state}`}</div>
                  {store.address && (
                    <StoreInfo>
                      <MapPin />
                      {store.address.length > 20 
                        ? `${store.address.substring(0, 20)}...` 
                        : store.address}
                    </StoreInfo>
                  )}
                </TableCell>
                <TableCell>{getStoreSalesGroupDisplay(store)}</TableCell>
                <TableCell>{store.sortField || (store as any).SortField || '-'}</TableCell>
                <TableCell>
                  <CustomerNumber>{store.StoreNumber || store.storeNumber || '-'}</CustomerNumber>
                </TableCell>
                <TableCell>{formatDate(store.lastNoteDate || store.lastVisit)}</TableCell>
                <TableCell>
                  <NotesBadge count={store.notesCount || 0}>
                    {store.notesCount || 0}
                  </NotesBadge>
                </TableCell>
                {showActions && (
                  <ActionsCell onClick={(e) => e.stopPropagation()}>
                    {onView && (
                      <ActionButton
                        onClick={() => onView(store)}
                        title={t('storeTable.viewDetails')}
                      >
                        <Eye />
                      </ActionButton>
                    )}
                    {onEdit && (
                      <ActionButton
                        onClick={() => onEdit(store)}
                        title={t('storeTable.editStore')}
                        style={{ color: ThemeColors.info.main }}
                      >
                        <Edit />
                      </ActionButton>
                    )}
                    {onAssignSalesRep && (
                      <ActionButton
                        onClick={() => onAssignSalesRep(store)}
                        title={t('storeTable.assignSalesRep')}
                      >
                        <UserCheck />
                      </ActionButton>
                    )}
                    {onDelete && (
                      <ActionButton
                        onClick={() => onDelete(store)}
                        title={t('storeTable.deleteStore')}
                        style={{ color: ThemeColors.error.main }}
                      >
                        <Trash2 />
                      </ActionButton>
                    )}
                  </ActionsCell>
                )}
              </TableRow>
            ))}
          </tbody>
        </Table>
      </TableScrollArea>
    </TableContainer>
  );
};

